<template>
  <div id="azanTable" class="w-full min-h-screen bg-cover bg-center p-4 sm:p-8 transition-all duration-500 relative"
    :style="{
      backgroundImage: `url(${backgroundImage})`,
      backgroundColor: bgOverlayColor,
      backgroundBlendMode: 'overlay'
    }">
    <!-- Header Section -->
    <header class="relative w-full text-white text-center pb-4 text-lg font-semibold flex flex-col items-center">
      <div class="flex items-center mb-2" :style="{ filter: `drop-shadow(0 4px 6px ${titleShadowColor})` }">
        <img src="/images/lq-logo.png" alt="Learn Quran" class="w-12 h-12 sm:w-16 sm:h-16">
      </div>
      <div class="space-y-1 mb-3">
        <h1 class="font-bold tracking-wide" :style="titleStyle">Bhatkal Azan Timing</h1>
        <h2 class="urdu-font" :style="titleStyle">بهٹکل اذان ٹائمینگ</h2>
      </div>
      <div class="py-2 px-8 rounded-full shadow-lg text-sm sm:text-base md:text-lg font-medium mb-2"
        :style="monthCardStyle">
        <span>{{ monthEnglish }}</span>
        <span class="mx-2">|</span>
        <span>{{ monthIslamic }}</span>
      </div>
    </header>

    <!-- Loading State -->
    <div v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 text-white text-center z-50">
      <div class="bg-gray-800 p-6 rounded-xl shadow-2xl">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
        <p class="text-lg">Loading prayer times...</p>
      </div>
    </div>

    <!-- Prayer Times Table -->
    <div v-else class="backdrop-blur-sm p-2 rounded-xl" :style="{ backgroundColor: tableBgColor }">
      <div class="overflow-x-auto">
        <table class="w-full border-collapse text-center rounded-lg overflow-hidden"
          :style="{ fontSize: tableFontSize + 'px' }">
          <thead>
            <tr>
              <th v-for="header in tableHeaders" :key="header.key" :style="headerCellStyle">{{ header.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, index) in prayerTimes" :key="index" :style="getRowStyle(entry.day)">
              <td :style="cellStyle"><span
                  v-if="entry.islamicDate === 1 || (prayerTimes[index + 1] && prayerTimes[index + 1].islamicDate === 1)">{{
                  monthIslamic.charAt(0) }}</span>{{ entry.islamicDate }}</td>
              <td :style="cellStyle"><span
                  v-if="entry.date === '01' || (prayerTimes[index + 1] && prayerTimes[index + 1].date === '01')">{{
                    entry.monthFirstChar }}</span>{{ entry.date }}</td>
              <td :style="cellStyle">{{ entry.day }}</td>
              <td :style="cellStyle">{{ entry.fajr }}</td>
              <td :style="cellStyle">{{ entry.sunrise }}</td>
              <td :style="cellStyle">{{ entry.dhuhr }}</td>
              <td :style="cellStyle">{{ entry.asr }}</td>
              <td :style="cellStyle">{{ entry.maghrib }}</td>
              <td :style="cellStyle">{{ entry.isha }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Buttons -->
    <template v-if="!screenshotMode">
      <button @click="showCustomizer = true"
        class="fixed bottom-4 right-4 p-3 sm:p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full shadow-lg z-40">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      <button @click="showDatePicker = true"
        class="fixed bottom-4 left-4 p-3 sm:p-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full shadow-lg z-40">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      <button @click="screenshotMode = true; exitTapCount = 0"
        class="fixed bottom-20 right-4 p-3 sm:p-4 bg-gradient-to-r from-gray-600 to-gray-800 text-white rounded-full shadow-lg z-40">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
    </template>

    <div v-if="screenshotMode" @click="handleExitTap" class="fixed bottom-0 right-0 w-16 h-16 z-50"
      style="opacity:0;-webkit-tap-highlight-color:transparent;"></div>
    <transition name="fade-quick">
      <div v-if="screenshotMode && exitTapCount > 0 && exitTapCount < 3"
        class="fixed bottom-20 right-4 bg-black bg-opacity-60 text-white text-xs px-3 py-1 rounded-full z-50 pointer-events-none">
        Tap {{ 3 - exitTapCount }} more time{{ exitTapCount === 2 ? '' : 's' }} to exit
      </div>
    </transition>

    <!-- ═══ CUSTOMIZATION PANEL ═══ -->
    <div v-if="showCustomizer" class="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-t-2xl z-10">
          <h3 class="text-xl font-bold flex justify-between items-center">
            Customize UI
            <button @click="showCustomizer = false" class="hover:bg-white hover:bg-opacity-20 rounded-lg p-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </h3>
        </div>

        <div class="p-4 sm:p-6 space-y-6">

          <!-- Floating RGBA Picker Popup -->
          <div v-if="activePicker" class="fixed inset-0 z-[60] flex items-center justify-center p-4"
            @click.self="activePicker = null">
            <div class="bg-white rounded-2xl shadow-2xl p-4 w-72">
              <div class="flex justify-between items-center mb-3">
                <p class="font-semibold text-gray-700 text-sm">{{ activePicker.label }}</p>
                <button @click="activePicker = null" class="text-gray-400 hover:text-gray-700">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <!-- SB Box -->
              <div class="relative w-full rounded-lg mb-3 cursor-crosshair" style="height:160px;"
                :style="{ background: `linear-gradient(to bottom, rgba(0,0,0,0), #000), linear-gradient(to right, #fff, hsl(${pickerHue},100%,50%))` }"
                @mousedown="startSBDrag" @touchstart.prevent="startSBDrag" ref="sbBox">
                <div class="absolute w-4 h-4 rounded-full border-2 border-white shadow pointer-events-none"
                  style="transform: translate(-50%, -50%)"
                  :style="{ left: pickerS + '%', top: (100 - pickerB) + '%', backgroundColor: pickerHexNoAlpha }"></div>
              </div>
              <!-- Hue slider -->
              <div class="mb-2">
                <label class="text-xs text-gray-500 mb-1 block">Hue</label>
                <input type="range" v-model="pickerHue" min="0" max="360" step="1" class="w-full"
                  style="background: linear-gradient(to right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00); height:10px; border-radius:5px; appearance:none; -webkit-appearance:none;">
              </div>
              <!-- Alpha slider -->
              <div class="mb-3">
                <label class="text-xs text-gray-500 mb-1 block">Opacity — {{ pickerA }}%</label>
                <div class="relative rounded overflow-hidden" style="height:14px;">
                  <div class="absolute inset-0"
                    style="background:repeating-conic-gradient(#ccc 0% 25%,#fff 0% 50%) 0 0/8px 8px;"></div>
                  <div class="absolute inset-0 rounded"
                    :style="{ background: `linear-gradient(to right, transparent, ${pickerHexNoAlpha})` }"></div>
                  <input type="range" v-model="pickerA" min="0" max="100" step="1"
                    class="absolute inset-0 w-full cursor-pointer" style="opacity:0; height:14px;">
                </div>
              </div>
              <!-- Preview + hex -->
              <div class="flex items-center gap-2 mb-3">
                <div class="w-10 h-10 rounded-lg border flex-shrink-0 overflow-hidden"
                  style="background:repeating-conic-gradient(#ccc 0% 25%,#fff 0% 50%) 0 0/8px 8px;">
                  <div class="w-full h-full rounded-lg" :style="{ backgroundColor: pickerRgba }"></div>
                </div>
                <input type="text" :value="pickerHexFull" @change="parseHexInput($event.target.value)"
                  class="flex-1 border rounded-lg px-2 py-1 text-xs font-mono focus:ring-2 focus:ring-purple-400 outline-none"
                  placeholder="#rrggbbaa">
              </div>
              <button @click="applyPicker"
                class="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-xl font-medium text-sm">✅
                Apply</button>
            </div>
          </div>

          <!-- Background -->
          <section class="space-y-3">
            <div class="flex items-center gap-2 border-b pb-2">
              <span class="text-lg">🖼️</span>
              <div>
                <h4 class="font-semibold text-gray-800">Background</h4>
                <p class="text-xs text-gray-400">Page background image and overlay tint</p>
              </div>
            </div>
            <button @click="triggerFileInput"
              class="w-full bg-gradient-to-r from-gray-100 to-gray-200 p-4 rounded-lg border-2 border-dashed border-gray-400 hover:border-purple-500 transition-colors">
              <span class="block text-gray-600 font-medium">📁 Upload Background Image</span>
              <span class="text-xs text-gray-400 mt-1 block">JPG, PNG, WEBP</span>
            </button>
            <color-swatch label="🎨 Tint Colour" :color="bgOverlayColor"
              @open="openPicker('bgOverlayColor', '🎨 Background Tint')" />
          </section>

          <!-- Title -->
          <section class="space-y-3">
            <div class="flex items-center gap-2 border-b pb-2">
              <span class="text-lg">✍️</span>
              <div>
                <h4 class="font-semibold text-gray-800">Title Text</h4>
                <p class="text-xs text-gray-400">English & Urdu title</p>
              </div>
            </div>
            <color-swatch label="🔤 Text Colour" :color="titleColor"
              @open="openPicker('titleColor', '🔤 Title Text')" />
            <color-swatch label="🌑 Shadow Colour" :color="titleShadowColor"
              @open="openPicker('titleShadowColor', '🌑 Title Shadow')" />
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1">📐 Font Size — {{ titleFontSize }}px</label>
              <input type="range" v-model="titleFontSize" min="16" max="48" step="2" class="w-full accent-purple-500">
              <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>Small</span><span>Large</span></div>
            </div>
          </section>

          <!-- Month Badge -->
          <section class="space-y-3">
            <div class="flex items-center gap-2 border-b pb-2">
              <span class="text-lg">📅</span>
              <div>
                <h4 class="font-semibold text-gray-800">Month Badge</h4>
                <p class="text-xs text-gray-400">Pill-shaped month label</p>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <color-swatch label="🌈 Left" :color="monthGradientStart"
                @open="openPicker('monthGradientStart', '🌈 Gradient Left')" />
              <color-swatch label="🌈 Middle" :color="monthGradientMid"
                @open="openPicker('monthGradientMid', '🌈 Gradient Middle')" />
              <color-swatch label="🌈 Right" :color="monthGradientEnd"
                @open="openPicker('monthGradientEnd', '🌈 Gradient Right')" />
            </div>
            <color-swatch label="🔤 Text Colour" :color="monthTextColor"
              @open="openPicker('monthTextColor', '🔤 Month Text')" />
            <div>
              <p class="text-xs text-gray-400 mb-1">👁️ Live preview</p>
              <div class="py-2 px-8 rounded-full text-center text-sm font-medium" :style="monthCardStyle">
                {{ monthEnglish || 'February 2026' }} | {{ monthIslamic || "Sha'ban 1447H" }}
              </div>
            </div>
          </section>

          <!-- Table -->
          <section class="space-y-3">
            <div class="flex items-center gap-2 border-b pb-2">
              <span class="text-lg">📋</span>
              <div>
                <h4 class="font-semibold text-gray-800">Prayer Times Table</h4>
                <p class="text-xs text-gray-400">Main table appearance</p>
              </div>
            </div>
            <color-swatch label="🎨 Table Background" :color="tableBgColor"
              @open="openPicker('tableBgColor', '🎨 Table Background')" />
            <color-swatch label="🔖 Header Row" :color="headerColor"
              @open="openPicker('headerColor', '🔖 Header Row')" />
            <color-swatch label="🔤 Text Colour" :color="tableTextColor"
              @open="openPicker('tableTextColor', '🔤 Table Text')" />
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1">📐 Font Size — {{ tableFontSize }}px</label>
              <input type="range" v-model="tableFontSize" min="8" max="24" step="1" class="w-full accent-purple-500">
              <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>Tiny</span><span>Large</span></div>
            </div>

            <!-- Cell Padding -->
            <div class="bg-indigo-50 border border-indigo-100 rounded-xl p-3 space-y-3">
              <div class="flex items-center gap-2">
                <span>📦</span>
                <div>
                  <p class="text-sm font-semibold text-indigo-800">Cell Padding</p>
                  <p class="text-xs text-indigo-400">Spacing inside every cell</p>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1">↕️ Row Padding — {{ cellPaddingY
                  }}px</label>
                <input type="range" v-model="cellPaddingY" min="0" max="20" step="1" class="w-full accent-indigo-500">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1">↔️ Column Padding — {{ cellPaddingX
                  }}px</label>
                <input type="range" v-model="cellPaddingX" min="0" max="24" step="1" class="w-full accent-indigo-500">
              </div>
              <div>
                <p class="text-xs text-gray-400 mb-1">👁️ Live preview</p>
                <table class="w-full border-collapse text-center text-xs rounded overflow-hidden">
                  <thead>
                    <tr>
                      <th v-for="h in ['Date', 'Day', 'Fajr', 'Maghrib']" :key="h" :style="headerCellStyle">{{ h }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td v-for="v in ['01', 'Mon', '05:12', '18:34']" :key="v + 'a'" :style="cellStyle">{{ v }}</td>
                    </tr>
                    <tr>
                      <td v-for="v in ['02', 'Tue', '05:13', '18:35']" :key="v + 'b'" :style="cellStyle">{{ v }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Borders toggle -->
            <div class="bg-gray-50 rounded-lg p-3">
              <label class="flex items-center gap-3 cursor-pointer select-none">
                <div class="relative">
                  <input type="checkbox" v-model="showBorders" class="sr-only">
                  <div class="w-10 h-5 rounded-full transition-colors"
                    :class="showBorders ? 'bg-purple-500' : 'bg-gray-300'"></div>
                  <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                    :class="showBorders ? 'translate-x-5' : 'translate-x-0'"></div>
                </div>
                <span class="text-sm font-medium text-gray-700">Show row divider lines</span>
              </label>
            </div>
            <color-swatch v-if="showBorders" label="📏 Divider Line" :color="borderColor"
              @open="openPicker('borderColor', '📏 Divider Line')" />
          </section>

          <!-- Friday -->
          <section class="space-y-3">
            <div class="flex items-center gap-2 border-b pb-2">
              <span class="text-lg">🌟</span>
              <div>
                <h4 class="font-semibold text-gray-800">Friday Highlight</h4>
                <p class="text-xs text-gray-400">Jumu'ah row colour</p>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <color-swatch label="🌈 Left" :color="fridayGradientStart"
                @open="openPicker('fridayGradientStart', '🌈 Friday Left')" />
              <color-swatch label="🌈 Middle" :color="fridayGradientMid"
                @open="openPicker('fridayGradientMid', '🌈 Friday Middle')" />
              <color-swatch label="🌈 Right" :color="fridayGradientEnd"
                @open="openPicker('fridayGradientEnd', '🌈 Friday Right')" />
            </div>
            <color-swatch label="🔤 Text Colour" :color="fridayTextColor"
              @open="openPicker('fridayTextColor', '🔤 Friday Text')" />
            <div>
              <p class="text-xs text-gray-400 mb-1">👁️ Live preview</p>
              <div class="py-2 px-4 rounded-lg text-center text-sm font-medium" :style="fridayPreviewStyle">
                ✨ Friday (Jumu'ah) — Sample Row
              </div>
            </div>
          </section>

          <!-- Screenshot tip -->
          <div class="bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-2">
            <span class="text-lg flex-shrink-0">📸</span>
            <div>
              <p class="text-sm font-medium text-blue-700">Screenshot Mode</p>
              <p class="text-xs text-blue-500 mt-0.5">Tap 📷 to hide controls. <strong>Triple-tap</strong> bottom-right
                to
                exit.</p>
            </div>
          </div>

          <div class="flex gap-3 pt-2">
            <button @click="resetToDefaults" class="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-medium">🔄
              Reset
              Defaults</button>
            <button @click="saveCustomizations"
              class="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-medium shadow-md">✅
              Apply Changes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Date Picker Popup -->
    <div v-if="showDatePicker" class="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div class="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-4 rounded-t-2xl">
          <h3 class="text-xl font-bold flex justify-between items-center">
            📅 Select Month to Display
            <button @click="showDatePicker = false" class="hover:bg-white hover:bg-opacity-20 rounded-lg p-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </h3>
        </div>
        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">📆 Hijri Year</label>
            <input type="number" v-model="selectedYear"
              class="w-full border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 outline-none">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">🌙 Islamic Month</label>
            <select v-model="selectedIslamicMonth"
              class="w-full border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 outline-none bg-white">
              <option value="" disabled>— Choose a month —</option>
              <option v-for="(month, index) in islamicMonths" :key="index" :value="month">{{ month }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">📅 Start Date (English)</label>
            <input type="date" v-model="selectedEnglishDate"
              class="w-full border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 outline-none">
          </div>
          <div class="flex gap-3 pt-2">
            <button @click="showDatePicker = false"
              class="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium">Cancel</button>
            <button @click="confirmDateSelection"
              class="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-medium shadow-md">✅
              Load Times</button>
          </div>
        </div>
      </div>
    </div>

    <input type="file" accept="image/*" ref="fileInput" class="hidden" @change="uploadBackground">
  </div>
</template>

<script>
import { h } from 'vue'

// ── ColorSwatch: uses render function instead of template string ─────────────
const ColorSwatch = {
  name: 'ColorSwatch',
  props: { label: String, color: String },
  emits: ['open'],
  render() {
    return h('div', [
      h('label', { class: 'block text-sm font-medium text-gray-600 mb-1' }, this.label),
      h('button', {
        onClick: () => this.$emit('open'),
        class: 'flex items-center gap-3 w-full border rounded-xl p-2 hover:border-purple-400 transition-colors bg-gray-50',
      }, [
        // Checkerboard wrapper
        h('div', {
          class: 'w-10 h-8 rounded-lg border flex-shrink-0 overflow-hidden',
          style: 'background:repeating-conic-gradient(#ccc 0% 25%,#fff 0% 50%) 0 0/10px 10px',
        }, [
          // Actual color fill
          h('div', { class: 'w-full h-full', style: { backgroundColor: this.color } }),
        ]),
        h('span', { class: 'text-xs font-mono text-gray-500 flex-1 text-left' }, this.color),
        h('span', { class: 'text-xs text-purple-500 font-medium pr-1' }, 'Edit ›'),
      ]),
    ])
  },
}

// ── HSV ↔ RGB helpers ────────────────────────────────────────────────────────
function hsvToRgb(h, s, v) {
  s /= 100; v /= 100
  const i = Math.floor(h / 60) % 6
  const f = h / 60 - Math.floor(h / 60)
  const p = v * (1 - s), q = v * (1 - f * s), t = v * (1 - (1 - f) * s)
  const m = [[v, t, p], [q, v, p], [p, v, t], [p, q, v], [t, p, v], [v, p, q]][i]
  return m.map(x => Math.round(x * 255))
}

function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min
  let h = 0
  if (d) {
    if (max === r) h = ((g - b) / d + 6) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  return [Math.round(h), max ? Math.round(d / max * 100) : 0, Math.round(max * 100)]
}

function parseRgba(str) {
  let m
  if ((m = str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)))
    return [+m[1], +m[2], +m[3], m[4] !== undefined ? Math.round(+m[4] * 100) : 100]
  if ((m = str.trim().match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i)))
    return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16), m[4] ? Math.round(parseInt(m[4], 16) / 255 * 100) : 100]
  return [0, 0, 0, 100]
}

function toRgba(r, g, b, a) {
  return `rgba(${r},${g},${b},${(a / 100).toFixed(2)})`
}

export default {
  name: 'HomeMain',
  components: { ColorSwatch },

  data() {
    return {
      // app state
      prayerTimes: [], isLoading: true,
      showDatePicker: false, showCustomizer: false,
      screenshotMode: false, exitTapCount: 0, exitTapTimer: null,

      selectedYear: new Date().getFullYear(),
      selectedIslamicMonth: '', selectedEnglishDate: '',
      islamicMonths: ['Muharram', 'Safar', 'Rabi Al-Awwal', 'Rabi Al-Thani',
        'Jumada Al-Awwal', 'Jumada Al-Thani', 'Rajab', "Sha'ban",
        'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah'],
      monthEnglish: '', monthIslamic: '',

      tableHeaders: [
        { key: 'islamicDate', label: 'Is. Date' }, { key: 'date', label: 'Date' },
        { key: 'day', label: 'Day' }, { key: 'fajr', label: 'Fajr' },
        { key: 'sunrise', label: 'Sunrise' }, { key: 'dhuhr', label: 'Dhuhr' },
        { key: 'asr', label: 'Asr' }, { key: 'maghrib', label: 'Maghrib' }, { key: 'isha', label: 'Isha' },
      ],

      // colours — all rgba strings
      backgroundImage: '/images/bg-1.jpg',
      bgOverlayColor: 'rgba(0,0,0,0.50)',
      titleColor: 'rgba(255,255,255,1.00)',
      titleShadowColor: 'rgba(0,0,0,1.00)',
      titleFontSize: 24,
      monthGradientStart: 'rgba(31,41,55,1.00)',
      monthGradientMid: 'rgba(55,65,81,1.00)',
      monthGradientEnd: 'rgba(17,24,39,1.00)',
      monthTextColor: 'rgba(255,255,255,1.00)',
      tableBgColor: 'rgba(0,0,0,0.70)',
      headerColor: 'rgba(75,85,99,1.00)',
      tableTextColor: 'rgba(255,255,255,1.00)',
      tableFontSize: 12,
      showBorders: true,
      borderColor: 'rgba(75,85,99,1.00)',
      cellPaddingY: 4,
      cellPaddingX: 8,
      fridayGradientStart: 'rgba(16,185,129,1.00)',
      fridayGradientMid: 'rgba(6,182,212,1.00)',
      fridayGradientEnd: 'rgba(59,130,246,1.00)',
      fridayTextColor: 'rgba(255,255,255,1.00)',

      // picker state
      activePicker: null,
      pickerHue: 0,
      pickerS: 0,
      pickerB: 100,
      pickerA: 100,
      sbDragging: false,
    }
  },

  computed: {
    pickerRgbArr() { return hsvToRgb(this.pickerHue, this.pickerS, this.pickerB) },
    pickerHexNoAlpha() {
      const [r, g, b] = this.pickerRgbArr
      return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
    },
    pickerRgba() {
      const [r, g, b] = this.pickerRgbArr
      return toRgba(r, g, b, this.pickerA)
    },
    pickerHexFull() {
      const [r, g, b] = this.pickerRgbArr
      const a = Math.round(this.pickerA / 100 * 255)
      return '#' + [r, g, b, a].map(x => x.toString(16).padStart(2, '0')).join('')
    },
    titleStyle() {
      return { color: this.titleColor, fontSize: this.titleFontSize + 'px', textShadow: `2px 2px 4px ${this.titleShadowColor}`, fontWeight: 'bold' }
    },
    monthCardStyle() {
      return { background: `linear-gradient(135deg,${this.monthGradientStart},${this.monthGradientMid},${this.monthGradientEnd})`, color: this.monthTextColor, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)' }
    },
    headerCellStyle() {
      return { backgroundColor: this.headerColor, color: this.tableTextColor, padding: `${this.cellPaddingY}px ${this.cellPaddingX}px`, fontSize: 'inherit', fontWeight: '600' }
    },
    cellStyle() {
      return { color: this.tableTextColor, fontSize: this.tableFontSize + 'px', padding: `${this.cellPaddingY}px ${this.cellPaddingX}px`, borderBottom: this.showBorders ? `1px solid ${this.borderColor}` : 'none' }
    },
    fridayPreviewStyle() {
      return { background: `linear-gradient(135deg,${this.fridayGradientStart},${this.fridayGradientMid},${this.fridayGradientEnd})`, color: this.fridayTextColor }
    },
  },

  methods: {
    // ── Picker ──────────────────────────────────────────────────────────────
    openPicker(key, label) {
      const [r, g, b, a] = parseRgba(this[key])
      const [h, s, v] = rgbToHsv(r, g, b)
      this.pickerHue = h
      this.pickerS = s
      this.pickerB = v
      this.pickerA = a
      this.activePicker = { key, label }
    },
    applyPicker() {
      this[this.activePicker.key] = this.pickerRgba
      this.activePicker = null
    },

    // ── SB drag ─────────────────────────────────────────────────────────────
    startSBDrag(e) {
      this.sbDragging = true
      this.updateSB(e)
      const move = ev => { if (this.sbDragging) this.updateSB(ev) }
      const up = () => {
        this.sbDragging = false
        window.removeEventListener('mousemove', move)
        window.removeEventListener('mouseup', up)
        window.removeEventListener('touchmove', move)
        window.removeEventListener('touchend', up)
      }
      window.addEventListener('mousemove', move)
      window.addEventListener('mouseup', up)
      window.addEventListener('touchmove', move, { passive: false })
      window.addEventListener('touchend', up)
    },
    updateSB(e) {
      const box = this.$refs.sbBox
      if (!box) return
      const rect = box.getBoundingClientRect()
      const cx = e.touches ? e.touches[0].clientX : e.clientX
      const cy = e.touches ? e.touches[0].clientY : e.clientY
      this.pickerS = Math.max(0, Math.min(100, Math.round((cx - rect.left) / rect.width * 100)))
      this.pickerB = Math.max(0, Math.min(100, Math.round(100 - (cy - rect.top) / rect.height * 100)))
    },
    parseHexInput(val) {
      const [r, g, b, a] = parseRgba(val)
      const [h, s, v] = rgbToHsv(r, g, b)
      this.pickerHue = h; this.pickerS = s; this.pickerB = v; this.pickerA = a
    },

    // ── App ─────────────────────────────────────────────────────────────────
    handleExitTap() {
      this.exitTapCount++
      clearTimeout(this.exitTapTimer)
      if (this.exitTapCount >= 3) { this.screenshotMode = false; this.exitTapCount = 0 }
      else this.exitTapTimer = setTimeout(() => { this.exitTapCount = 0 }, 2000)
    },
    triggerFileInput() { this.$refs.fileInput.click() },
    uploadBackground(event) {
      const file = event.target.files[0]
      if (file) { this.backgroundImage = URL.createObjectURL(file); this.extractColorsFromImage(file) }
    },
    extractColorsFromImage(file) {
      const img = new Image()
      img.crossOrigin = 'Anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = img.width; canvas.height = img.height
        ctx.drawImage(img, 0, 0)
        const data = ctx.getImageData(0, 0, 100, 100).data
        let r = 0, g = 0, b = 0
        for (let i = 0; i < data.length; i += 4) { r += data[i]; g += data[i + 1]; b += data[i + 2] }
        const px = data.length / 4
        r = Math.round(r / px); g = Math.round(g / px); b = Math.round(b / px)
        const adj = (c, p) => Math.min(255, Math.max(0, Math.round(c + c * p / 100)))
        this.bgOverlayColor = toRgba(r, g, b, 50)
        this.tableBgColor = toRgba(r, g, b, 70)
        this.headerColor = toRgba(adj(r, -30), adj(g, -30), adj(b, -30), 100)
        this.fridayGradientStart = toRgba(adj(r, 20), adj(g, 20), adj(b, 20), 100)
        this.fridayGradientMid = toRgba(r, g, b, 100)
        this.fridayGradientEnd = toRgba(adj(r, -20), adj(g, -20), adj(b, -20), 100)
      }
      img.src = URL.createObjectURL(file)
    },
    confirmDateSelection() {
      localStorage.setItem('selectedEnglishDate', this.selectedEnglishDate)
      localStorage.setItem('selectedIslamicMonth', this.selectedIslamicMonth)
      localStorage.setItem('selectedYear', this.selectedYear)
      this.showDatePicker = false; this.refreshData()
    },
    async updateMonthDetails() {
      const s = localStorage.getItem('selectedEnglishDate')
      const d = s ? new Date(s) : new Date()
      this.monthEnglish = d.toLocaleString('en-US', { month: 'long', year: 'numeric' })
      await this.fetchHijriDate()
    },
    async fetchHijriDate() {
      try {
        const s = localStorage.getItem('selectedEnglishDate')
        const d = s ? new Date(s) : new Date()
        const fmt = `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`
        const res = await fetch(`https://api.aladhan.com/v1/gToH?date=${fmt}`)
        const data = await res.json()
        this.monthIslamic = (data.data && data.data.hijri) ? `${data.data.hijri.month.en} ${data.data.hijri.year}H` : 'Error'
      } catch (err) {
        console.error(err)
        this.monthIslamic = 'Error fetching Hijri date'
      }
    },
    async fetchPrayerTimes() {
      try {
        this.isLoading = true
        const s = localStorage.getItem('selectedEnglishDate')
        const d = s ? new Date(s) : new Date()
        const start = d.toISOString().split('T')[0]
        const res = await fetch(`https://api.azantimeapp.com/api/user/azan-timings?start_date=${start}`)
        const data = await res.json()
        if (data.response.azan_timings) {
          this.prayerTimes = data.response.azan_timings.map((day, i) => ({
            islamicDate: i + 1, date: day.date.split('-')[2],
            monthFirstChar: new Date(day.date).toLocaleString('en-US', { month: 'short' }).charAt(0),
            day: new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' }),
            fajr: day.fajr.slice(0, 5), sunrise: day.sunrise.slice(0, 5),
            dhuhr: day.dhuhr.slice(0, 5), asr: day.asr.slice(0, 5),
            maghrib: day.maghrib.slice(0, 5), isha: day.isha.slice(0, 5),
          }))
        }
      } catch (err) { console.error(err) }
      finally { this.isLoading = false }
    },
    getRowStyle(day) {
      if (day !== 'Fri') return {}
      return { background: `linear-gradient(135deg,${this.fridayGradientStart},${this.fridayGradientMid},${this.fridayGradientEnd})`, color: this.fridayTextColor }
    },
    resetToDefaults() {
      Object.assign(this, {
        bgOverlayColor: 'rgba(0,0,0,0.50)',
        titleColor: 'rgba(255,255,255,1.00)', titleShadowColor: 'rgba(0,0,0,1.00)', titleFontSize: 24,
        monthGradientStart: 'rgba(31,41,55,1.00)', monthGradientMid: 'rgba(55,65,81,1.00)', monthGradientEnd: 'rgba(17,24,39,1.00)',
        monthTextColor: 'rgba(255,255,255,1.00)',
        tableBgColor: 'rgba(0,0,0,0.70)', headerColor: 'rgba(75,85,99,1.00)',
        tableTextColor: 'rgba(255,255,255,1.00)', tableFontSize: 12,
        showBorders: true, borderColor: 'rgba(75,85,99,1.00)',
        cellPaddingY: 4, cellPaddingX: 8,
        fridayGradientStart: 'rgba(16,185,129,1.00)', fridayGradientMid: 'rgba(6,182,212,1.00)',
        fridayGradientEnd: 'rgba(59,130,246,1.00)', fridayTextColor: 'rgba(255,255,255,1.00)',
        backgroundImage: '/images/bg-1.jpg',
      })
    },
    saveCustomizations() {
      const keys = ['bgOverlayColor', 'titleColor', 'titleShadowColor', 'titleFontSize',
        'monthGradientStart', 'monthGradientMid', 'monthGradientEnd', 'monthTextColor',
        'tableBgColor', 'headerColor', 'tableTextColor', 'tableFontSize',
        'showBorders', 'borderColor', 'cellPaddingY', 'cellPaddingX',
        'fridayGradientStart', 'fridayGradientMid', 'fridayGradientEnd', 'fridayTextColor', 'backgroundImage']
      const out = {}
      keys.forEach(k => { out[k] = this[k] })
      localStorage.setItem('uiCustomizations', JSON.stringify(out))
      this.showCustomizer = false
    },
    loadCustomizations() {
      const s = localStorage.getItem('uiCustomizations')
      if (s) {
        try { Object.assign(this, JSON.parse(s)) } catch (err) { console.error(err) }
      }
    },
    refreshData() { this.updateMonthDetails(); this.fetchPrayerTimes() },
  },

  mounted() {
    this.loadCustomizations()
    this.selectedEnglishDate = localStorage.getItem('selectedEnglishDate') || ''
    this.selectedIslamicMonth = localStorage.getItem('selectedIslamicMonth') || ''
    if (!this.selectedEnglishDate || !this.selectedIslamicMonth) this.showDatePicker = true
    else this.refreshData()
  },
}
</script>

<style scoped>
.urdu-font {
  font-family: 'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Urdu Typesetting', serif;
}

* {
  transition: background-color 0.3s, color 0.3s, border-color 0.3s, box-shadow 0.3s;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.fade-quick-enter-active {
  transition: opacity 0.2s;
}

.fade-quick-leave-active {
  transition: opacity 0.5s;
}

.fade-quick-enter-from,
.fade-quick-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  table {
    font-size: 10px;
  }
}
</style>